// Slamdunk Theme - Sunday Market (Seller/Garden Variant)
// "Earthy, Organic, & Friendly"

export const colors = {
  // Brand Colors (Seller/Garden Theme - Sage Green)
  brand: {
    primary: '#748C70',    // Sage Green
    secondary: '#E8B49C',  // Soft Clay
    tertiary: '#E0E2DB',   // Light Stone
  },

  // Merchant Colors (Store/Brick Theme - Terracotta)
  merchant: {
    primary: '#C07A5F',      // Deep Terracotta
    light: '#D4A088',        // Light Terracotta
    dark: '#8B5A48',         // Dark Terracotta
    background: '#FDF5F2',   // Very light terracotta bg
    border: 'rgba(192, 122, 95, 0.2)',
  },

  // Background colors
  background: {
    primary: '#F2F0E9',    // Warm Stone/Oatmeal (App BG)
    secondary: '#E0E2DB',  // Light Stone
    card: '#FFFFFF',       // Pure White (Cards)
    modal: '#FFFFFF',
    overlay: 'rgba(62, 66, 58, 0.4)',
    input: '#FFFFFF',
  },

  // Text colors
  text: {
    primary: '#3E423A',    // Dark Olive/Charcoal
    secondary: '#858B80',  // Muted Sage Grey
    inverse: '#FFFFFF',    // White text on dark
    error: '#D32F2F',
    success: '#388E3C',
    link: '#748C70',
  },

  // UI colors
  ui: {
    border: '#E0E2DB',
    borderLight: 'rgba(224, 226, 219, 0.5)',
    borderFocus: '#748C70',
    divider: '#F2F0E9',
    disabled: '#E0E0E0',
  },

  // Status colors
  status: {
    success: '#748C70',    // Sage Green
    warning: '#E8B49C',    // Clay
    error: '#D32F2F',      // Red
    info: '#5D7C89',       // Muted Blue
  },
}

export const shadows = {
  small: {
    shadowColor: '#3E423A',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  soft: {
    shadowColor: '#748C70',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  medium: {
    shadowColor: '#3E423A',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 8,
  },
  none: {
    shadowColor: 'transparent',
    elevation: 0,
  },
}

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 20, // Standard padding
  xl: 24,
  xxl: 32,
  xxxl: 48,
}

export const borderRadius = {
  sm: 6,
  md: 8,  // Buttons, inputs
  lg: 10, // Cards, sections
  xl: 16, // Bottom Sheet / Nav
  full: 9999,
}

export const typography = {
  // In React Native, we might need to load custom fonts. 
  // For now, we map to system weights that approximate the feel.
  h1: {
    fontSize: 28,
    fontWeight: '700' as const,
    color: colors.text.primary,
    letterSpacing: -0.5,
  },
  h2: {
    fontSize: 22,
    fontWeight: '700' as const,
    color: colors.text.primary,
  },
  h3: {
    fontSize: 18,
    fontWeight: '600' as const,
    color: colors.text.primary,
  },
  body: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '400' as const,
    color: colors.text.primary,
  },
  bodySmall: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '400' as const,
    color: colors.text.secondary,
  },
  caption: {
    fontSize: 12,
    fontWeight: '500' as const,
    color: colors.text.secondary,
  },
  button: {
    fontSize: 16,
    fontWeight: '600' as const,
    letterSpacing: 0.5,
  },
}

// Common component styles pre-configured
export const components = {
  button: {
    primary: {
      backgroundColor: colors.brand.primary,
      borderRadius: borderRadius.md,
      paddingVertical: 14,
      paddingHorizontal: spacing.lg,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      ...shadows.soft,
    },
    secondary: {
      backgroundColor: colors.background.card,
      borderRadius: borderRadius.md,
      paddingVertical: 12, // Border takes up space
      paddingHorizontal: spacing.lg,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      borderWidth: 2,
      borderColor: colors.brand.primary,
    },
    ghost: {
      backgroundColor: 'transparent',
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.md,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    },
    text: {
      primary: {
        color: colors.text.inverse,
        fontSize: 16,
        fontWeight: '600' as const,
      },
      secondary: {
        color: colors.brand.primary,
        fontSize: 16,
        fontWeight: '600' as const,
      },
      ghost: {
        color: colors.text.primary,
        fontSize: 16,
        fontWeight: '600' as const,
      },
    },
  },

  card: {
    container: {
      backgroundColor: colors.background.card,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      ...shadows.soft,
    },
    flat: {
      backgroundColor: 'transparent',
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      borderWidth: 1,
      borderColor: colors.ui.border,
    },
  },

  input: {
    container: {
      backgroundColor: colors.background.input,
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      borderWidth: 2,
      borderColor: colors.ui.border,
    },
    text: {
      fontSize: 16,
      color: colors.text.primary,
    },
    placeholder: colors.text.secondary,
  },

  header: {
    container: {
      backgroundColor: colors.background.primary, // Blends with body
      paddingTop: 60,
      paddingBottom: spacing.md,
      paddingHorizontal: spacing.lg,
    },
    title: {
      fontSize: 24,
      fontWeight: '700' as const,
      color: colors.text.primary,
    },
    subtitle: {
      fontSize: 14,
      color: colors.text.secondary,
    },
  },

  bottomNav: {
    container: {
      backgroundColor: colors.background.card,
      borderTopLeftRadius: borderRadius.xl,
      borderTopRightRadius: borderRadius.xl,
      ...shadows.medium,
      paddingBottom: 20,
      paddingTop: 10,
      borderTopWidth: 0,
    },
    item: {
      alignItems: 'center' as const,
      padding: spacing.sm,
    },
    activeItem: {
      backgroundColor: 'rgba(116, 140, 112, 0.15)', // Primary with opacity
      borderRadius: borderRadius.md,
    },
    activeColor: colors.brand.primary,
    inactiveColor: colors.text.secondary,
  },
}

const theme = {
  colors,
  shadows,
  spacing,
  borderRadius,
  typography,
  components,
}

export default theme

